// The motion layer (approved Motion board, 2026-10-15). Scroll-driven scenes use GSAP ScrollTrigger;
// everything else is CSS. Rules:
// - transform and opacity only; at most two pinned sections per page; pins on desktop only;
// - under prefers-reduced-motion nothing here runs: every element keeps its final, static state;
// - nothing runs for a headless browser either (navigator.webdriver), so the prerendered HTML
//   (scripts/prerender.mjs) is the final state, never a half-played animation with inline styles.
import { onMounted, onBeforeUnmount, Ref } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export { gsap, ScrollTrigger };

export const DESKTOP =
  "(min-width: 861px) and (prefers-reduced-motion: no-preference)";
export const PHONE =
  "(max-width: 860px) and (prefers-reduced-motion: no-preference)";

/** True when animations may run. A test can set window.__motion = true to see them under Playwright. */
export function motionAllowed(): boolean {
  if (typeof window === "undefined") return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (navigator.webdriver && !(window as any).__motion) return false;
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Builds a page's scroll scenes inside a gsap.matchMedia scoped to `scope`, so they are reverted on
 * unmount and rebuilt when the viewport crosses the phone/desktop line or reduced motion is switched on.
 */
export function useMotion(
  scope: Ref<HTMLElement | undefined>,
  build: (c: { desktop: boolean; phone: boolean }) => void | (() => void)
): void {
  let mm: gsap.MatchMedia | undefined;
  onMounted(() => {
    if (!motionAllowed() || !scope.value) return;
    mm = gsap.matchMedia(scope.value);
    mm.add({ desktop: DESKTOP, phone: PHONE }, (ctx) => {
      const c = ctx.conditions as { desktop: boolean; phone: boolean };
      if (c.desktop || c.phone) return build(c);
    });
    // Pin positions depend on the final text size.
    document.fonts?.ready.then(() => ScrollTrigger.refresh());
  });
  onBeforeUnmount(() => mm?.revert());
}

/** Elements rise into place once, when their container enters (sections, cards, bento waves). */
export function reveal(
  targets: gsap.TweenTarget,
  trigger: Element,
  opts: gsap.TweenVars = {}
): void {
  gsap.from(targets, {
    opacity: 0,
    y: 24,
    duration: 0.6,
    ease: "expo.out",
    stagger: 0.06,
    scrollTrigger: { trigger, start: "top 85%", once: true },
    ...opts
  });
}

/** Apple-style statement: each word goes from dim to lit as the paragraph crosses the viewport. */
export function wordHighlight(el: Element): void {
  gsap.fromTo(
    el.querySelectorAll(".hl"),
    { opacity: 0.18 },
    {
      opacity: 1,
      ease: "none",
      stagger: 0.1,
      scrollTrigger: {
        trigger: el,
        start: "top 75%",
        end: "bottom 45%",
        scrub: true
      }
    }
  );
}

/** Pauses an element's CSS loops while it is off screen (and while the tab is hidden, which browsers do anyway). */
export function useLoopPause(el: Ref<Element | undefined>): void {
  let io: IntersectionObserver | undefined;
  onMounted(() => {
    if (!el.value || typeof IntersectionObserver === "undefined") return;
    io = new IntersectionObserver(([e]) =>
      e.target.classList.toggle("paused", !e.isIntersecting)
    );
    io.observe(el.value);
  });
  onBeforeUnmount(() => io?.disconnect());
}

/** Splits a sentence into words for wordHighlight / letter animations, keeping spaces readable. */
export function words(text: string): string[] {
  return text.split(/\s+/).filter(Boolean);
}
